import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from '@/lib/gsap';
import { GRADE_META, xpFor } from '@/lib/score';
import type { Grade } from '@/lib/score';
import { useAppStore } from '@/lib/store';

export function ScoreBurst(): JSX.Element {
  const seq = useAppStore((s) => s.combo.burstSeq);
  const grade = useAppStore((s) => s.combo.lastGrade);
  const combo = useAppStore((s) => s.combo.combo);
  const [active, setActive] = useState<{ seq: number; g: Grade; xp: number; combo: number } | null>(null);

  useEffect(() => {
    if (grade === null || seq === 0) return;
    setActive({ seq, g: grade, xp: xpFor(grade, Math.max(0, combo - 1)), combo });
    const t = setTimeout(() => setActive(null), 1100);
    return () => clearTimeout(t);
  }, [seq, grade, combo]);

  return (
    <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden grid place-items-center">
      {active !== null && <BurstStage key={active.seq} grade={active.g} xp={active.xp} />}
    </div>
  );
}

function BurstStage({ grade, xp }: { grade: Grade; xp: number }): JSX.Element {
  const meta = GRADE_META[grade];
  const stageRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const xpRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const label = labelRef.current;
    const xpEl = xpRef.current;
    const flash = flashRef.current;
    const particles = particlesRef.current;
    if (!stage || !label || !xpEl || !flash || !particles) return;

    const isPositive = grade !== 'miss';
    const ringColor = meta.ringColor;
    const particleCount = grade === 'perfect' ? 36 : grade === 'great' ? 20 : 0;

    particles.innerHTML = '';
    const nodes: HTMLSpanElement[] = [];
    for (let i = 0; i < particleCount; i++) {
      const sp = document.createElement('span');
      sp.className = 'absolute rounded-sm';
      const size = 4 + Math.random() * 9;
      sp.style.width = `${size}px`;
      sp.style.height = `${size}px`;
      sp.style.backgroundColor = ringColor;
      sp.style.boxShadow = `0 0 14px ${ringColor}`;
      sp.style.top = '50%';
      sp.style.left = '50%';
      particles.appendChild(sp);
      nodes.push(sp);
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.fromTo(flash, { opacity: 0 }, {
        opacity: isPositive ? 0.55 : 0.35, duration: 0.08, ease: 'power2.out',
      }, 0);
      tl.to(flash, { opacity: 0, duration: 0.5, ease: 'power2.in' }, 0.1);

      tl.fromTo(label,
        { scale: 0.4, y: 40, opacity: 0, filter: 'blur(10px)' },
        { scale: 1.15, y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.45, ease: 'back.out(2.2)' },
        0.02,
      );
      tl.to(label, { scale: 1, duration: 0.18, ease: 'power2.out' }, '>');

      if (isPositive) {
        tl.to(label, {
          x: '+=2', y: '+=1', duration: 0.05, repeat: 5, yoyo: true, ease: 'none',
        }, 0.1);
      }

      nodes.forEach((node, i) => {
        const angle = (i / nodes.length) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const dist = 140 + Math.random() * 280;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        tl.fromTo(node,
          { x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 },
          { x: dx, y: dy, opacity: 0, scale: 0.3, rotate: Math.random() * 540,
            duration: 0.95 + Math.random() * 0.4, ease: 'expo.out' },
          0.06,
        );
      });

      if (isPositive) {
        const intensity = grade === 'perfect' ? 8 : 5;
        tl.to(document.body, {
          keyframes: [
            { x: -intensity, y: intensity * 0.6, duration: 0.04 },
            { x: intensity, y: -intensity * 0.5, duration: 0.04 },
            { x: -intensity * 0.6, y: -intensity, duration: 0.04 },
            { x: intensity * 0.4, y: intensity * 0.5, duration: 0.04 },
            { x: 0, y: 0, duration: 0.06 },
          ],
          ease: 'none',
        }, 0.05);
      }

      if (isPositive && xp > 0) {
        tl.fromTo(xpEl,
          { y: 0, opacity: 0, scale: 0.6 },
          { y: -60, opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2)' },
          0.4,
        );
        tl.to(xpEl, { y: -90, opacity: 0, duration: 0.35, ease: 'power2.in' }, '>');
      }

      tl.to(label, { opacity: 0, scale: 0.95, y: -10, duration: 0.32, ease: 'power2.in' }, 0.78);
    }, stage);

    return () => ctx.revert();
  }, [grade, xp, meta.ringColor]);

  return (
    <div ref={stageRef} className="relative grid place-items-center">
      <div
        ref={flashRef}
        className="absolute -inset-32 rounded-full blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(closest-side, ${meta.ringColor}AA, transparent 70%)` }}
      />
      <div ref={particlesRef} className="absolute inset-0 pointer-events-none grid place-items-center" />
      <div ref={labelRef} className="relative text-center px-3" style={{ opacity: 0 }}>
        <div
          // 5xl on tiny phones / 7xl tablet / 9xl desktop. Avoids overflow
          // when the grade is "PERFECT" on a 320px screen.
          className="font-score text-5xl sm:text-7xl md:text-9xl leading-none text-neon text-chromatic"
          style={{ color: meta.ringColor }}
        >
          {meta.label}
        </div>
        <div className={`mt-2 font-display text-lg md:text-2xl font-bold ${meta.color}`}>
          {meta.emoji} {meta.zh}
        </div>
      </div>
      <div
        ref={xpRef}
        className="absolute font-score text-3xl text-mint-300 text-neon pointer-events-none"
        style={{ opacity: 0 }}
      >
        +{xp} XP
      </div>
    </div>
  );
}
